import { useRoute, type RouteProp } from '@react-navigation/native';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useFilterTransformer } from '../../../hooks/useFilterTransformer';
import { usePaginatedState } from '../../../hooks/usePaginatedState';
import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import type { SignedInRootStackParamList } from '../../../routes/types';
import { batchTicketRedeemFilterState } from '../../../store/batchTicket/atom';
import {
  normalizeBatchTicketRedeemOffers,
  normalizeBatchTicketRedeemRows,
} from '../../../store/batchTicket/normalize';
import { batchTicketRedeemOffersQuery } from '../../../store/batchTicket/selectors/redeem';
import { useErrorToast } from '../hooks/toast-errpr';

interface RedeemOffersContextType {
  redeemOffersData: ReturnType<typeof normalizeBatchTicketRedeemOffers>;
  isLoadingRedeemOffers: boolean;
  isLoadingMoreRedeemOffers: boolean;
  searchTermRedeemOffers: string;
  handleSearchRedeemOffers: (term: string) => void;
  handleClearSearchRedeemOffers: () => void;
  onLoadingMoreRedeemOffers: () => Promise<void>;
  redeemOffersRefresher: () => void;
  redeemOffersIsError: boolean;
}

const RedeemOffersContext = createContext<RedeemOffersContextType>({} as RedeemOffersContextType);

export type RedeemItemProps =
  | ReturnType<typeof normalizeBatchTicketRedeemOffers>[number]
  | ReturnType<typeof normalizeBatchTicketRedeemRows>[number];

export const RedeemOffersProvider = ({ children }: { children: React.ReactNode }) => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'RedeemBatchTicketDetails'>>();
  const { handleErrorToast } = useErrorToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTermRedeemOffers, setSearchTermRedeemOffers] = useState('');
  const isFirstPage = currentPage === 1;

  const {
    appliedFilters: batchTicketRedeemAppliedFilters,
    availableFilters: batchTicketRedeemAvailableFilters,
  } = useRecoilValue(batchTicketRedeemFilterState);

  const { transformFilters } = useFilterTransformer(batchTicketRedeemAvailableFilters);

  const appliedFiltersBatchTickerRedeem = transformFilters(batchTicketRedeemAppliedFilters, {
    transformRangeFieldNames: true,
  });

  const {
    data: redeemOffersResponse,
    error: redeemOffersIsError,
    loading: isLoadingRedeemOffers,
    refresher: redeemOffersRefresher,
  } = useRecoilQuery(
    batchTicketRedeemOffersQuery({
      basketAdvisorId: params.basketAdvisorId,
      page: currentPage,
      searchTerm: searchTermRedeemOffers,
      ...appliedFiltersBatchTickerRedeem,
    }),
    { cachePolicy: 'no-cache' }
  );

  const [redeemOffersData, setRedeemOffersData] = usePaginatedState(
    redeemOffersResponse?.data,
    currentPage
  );

  const onLoadingMoreRedeemOffers = async () => {
    if (
      !redeemOffersResponse?.meta?.pagination.hasNext ||
      isLoadingRedeemOffers ||
      redeemOffersIsError
    ) {
      return;
    }

    setCurrentPage((prev) => prev + 1);
  };

  const handleSearchRedeemOffers = (term: string) => {
    setSearchTermRedeemOffers(term);
    setRedeemOffersData([]);
    setCurrentPage(1);
  };

  const handleClearSearchRedeemOffers = () => {
    setSearchTermRedeemOffers('');
    setCurrentPage(1);
  };

  useEffect(() => {
    handleErrorToast(redeemOffersIsError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeemOffersIsError]);

  return (
    <RedeemOffersContext.Provider
      value={{
        redeemOffersData,
        isLoadingRedeemOffers: isLoadingRedeemOffers && isFirstPage,
        isLoadingMoreRedeemOffers: isLoadingRedeemOffers && !isFirstPage,
        searchTermRedeemOffers,
        handleSearchRedeemOffers,
        handleClearSearchRedeemOffers,
        onLoadingMoreRedeemOffers,
        redeemOffersRefresher,
        redeemOffersIsError: !!redeemOffersIsError,
      }}>
      {children}
    </RedeemOffersContext.Provider>
  );
};

export const useRedeemOffersContext = (): RedeemOffersContextType => {
  const context = useContext(RedeemOffersContext);

  if (!context) {
    throw new Error('useRedeemOffersContext must be used within an RedeemOffersContext');
  }
  return context;
};
